import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  CheckCircle,
  MonetizationOn,
  Group,
  Speed,
  Security,
  Business,
  EmojiObjects,
  Lightbulb,
  WbSunny,
  PersonAdd,
  AccountBalance,
  Share,
  Payment,
  Support,
  Email,
} from "@mui/icons-material";

const features = [
  {
    icon: <MonetizationOn color="primary" />,
    title: "Global Transactions",
    description:
      "Fast and secure crypto transactions supported across multiple networks",
  },
  {
    icon: <Group color="primary" />,
    title: "Community Growth",
    description: "Join our growing community of renewable energy investors",
  },
  {
    icon: <Speed color="primary" />,
    title: "Instant Processing",
    description: "Experience quick deposits and 24-hour withdrawal processing",
  },
  {
    icon: <Security color="primary" />,
    title: "Enhanced Security",
    description: "Advanced encryption and secure wallet management systems",
  },
];

const partners = [
  {
    name: "Mainstream",
    logo: "/images/partners/mainstream.png", // Path starts from public folder
    description: "Leading global renewable energy developer",
  },
  {
    name: "Enel Green Power",
    logo: "/images/partners/Enel.png",
    description: "Global leader in renewable energy",
  },
  {
    name: "Vestas Wind Systems AS",
    logo: "/images/partners/vestas.png",
    description: "World's largest wind turbine manufacturer",
  },
  {
    name: "ACWA Power",
    logo: "/images/partners/acwa.png",
    description: "Leading developer of power generation projects",
  },
];

const howItWorks = [
  {
    step: "Create Your Account",
    description:
      "Sign up with your email, phone number and create a secure password. Complete the verification process through your email to activate your account.",
    icon: <PersonAdd color="primary" />,
  },
  {
    step: "Fund Your Wallet",
    description:
      "Make your first deposit using USDT on any supported network (TRC20, BEP20, or ERC20). Our platform automatically converts and credits your account in NGN.",
    icon: <AccountBalance color="primary" />,
  },
  {
    step: "Choose Your Network",
    description:
      "Select your preferred crypto network for transactions. We recommend TRC20 for lower fees and faster processing times. Each network has its own minimum requirements.",
    icon: <MonetizationOn color="primary" />,
  },
  {
    step: "Join Our Community",
    description:
      "Become part of our growing community of investors. Share insights and opportunities with fellow renewable energy enthusiasts.",
    icon: <Share color="primary" />,
  },
  {
    step: "Manage Your Portfolio",
    description:
      "Track your investments, process withdrawals, and manage your crypto assets through our intuitive dashboard.",
    icon: <Payment color="primary" />,
  },
  {
    step: "Get Expert Support",
    description:
      "Access our 24/7 customer support through WhatsApp or email. Our team is always ready to assist you with any questions or concerns.",
    icon: <Support color="primary" />,
  },
];

const LandingPage = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom>
                Join ATKLand
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Start earning with our powerful investment program and wallet
                earnings system
              </Typography>
              <Button
                component={RouterLink}
                to="/signup"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "grey.100",
                  },
                }}
              >
                Get Started
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6 }}>
          Why Choose Us
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ textAlign: "center", mb: 2 }}>
                    {React.cloneElement(feature.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" align="center" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: "grey.100", py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom>
            How It Works
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Start your investment journey in 6 simple steps
          </Typography>
          <Grid container spacing={4}>
            {howItWorks.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="h6">{item.step}</Typography>
                    </Box>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      {React.cloneElement(item.icon, {
                        sx: { fontSize: 40 },
                      })}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minHeight: 80 }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Partners Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6 }}>
          Our Partners
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {partners.map((partner) => (
            <Grid item xs={12} sm={6} md={3} key={partner.name}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: "center" }}>
                  <Box
                    component="img"
                    src={partner.logo}
                    alt={partner.name}
                    sx={{
                      height: 80,
                      objectFit: "contain",
                      mb: 2,
                      filter: "grayscale(100%)",
                      transition: "filter 0.3s",
                      "&:hover": {
                        filter: "grayscale(0%)",
                      },
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {partner.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {partner.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* About Us Section */}
      <Box sx={{ bgcolor: "grey.100", py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6 }}>
            About Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom color="primary">
                Sustainable Energy Solutions
              </Typography>
              <Typography paragraph>
                ATKLand is at the forefront of renewable energy investment and
                distribution in Africa. We partner with leading global renewable
                energy companies to bring sustainable power solutions to homes
                and businesses across Africa.
              </Typography>
              <Typography paragraph>
                Our mission is to accelerate the adoption of clean energy while
                creating investment opportunities for everyday African through
                our innovative platform. We are committed to providing
                affordable, reliable, and sustainable energy solutions to
                communities across the continent.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/images/solar-panel.jpeg" // Path starts from public folder
                sx={{ width: "100%", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How We Make Money Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ mb: 6 }}>
          Our Business Model
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
            <Box
              component="img"
              src="/images/bussiness-model.jpg" // Path starts from public folder
              sx={{ width: "100%", borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <WbSunny color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Solar Installation"
                  secondary="We install and maintain solar systems for residential and commercial clients"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Business color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Energy Distribution"
                  secondary="We operate mini-grids and distribute renewable energy to communities"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmojiObjects color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Consulting Services"
                  secondary="We provide renewable energy consulting to businesses"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Lightbulb color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Investment Returns"
                  secondary="We share profits with our platform investors through our bonus wallet system"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* Footer Section */}
      <Box sx={{ bgcolor: "primary.dark", color: "white", pt: 8, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} justifyContent="space-between">
            <Grid item xs={12} md={5}>
              <Typography variant="h6" gutterBottom>
                About ATKLand
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Africa's leading renewable energy investment platform. We
                connect investors with sustainable energy projects while
                promoting clean energy adoption and distribution through our
                innovative crypto-powered platform.
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom>
                Quick Links
              </Typography>
              <List sx={{ p: 0 }}>
                {["Home", "About Us", "How It Works", "Support"].map((text) => (
                  <ListItem key={text} sx={{ p: 0, mb: 1 }}>
                    <Button
                      component={RouterLink}
                      to={`/${text.toLowerCase().replace(" ", "-")}`}
                      sx={{
                        color: "white",
                        opacity: 0.7,
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      {text}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Contact Us
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: 0.7,
                  mb: 2,
                }}
              >
                <Email sx={{ mr: 2 }} />
                <Typography variant="body2">support@atkland.com</Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 2 }}>
                For inquiries and support, please email us. We typically respond
                within 24 hours.
              </Typography>
            </Grid>
          </Grid>

          <Box
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              mt: 4,
              pt: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              © {new Date().getFullYear()} ATKLand. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
